import type { EmotionLabel } from '../../types';
import type { EmotionScore } from './Scorer';
import type { EyeReliabilityContext } from './EyeContextAnalyzer';
import {
  computeFrameSignals, detectArtifacts, selectWinner, applyMobileStreaks,
  applyPersistence, computeSmoothedOutput,
} from './EmotionProcessorService';

export type { EmotionScore } from './Scorer';

export interface SmoothedState {
  emotion: EmotionLabel;
  valence: number;
  arousal: number;
  confidence: number;
}

export interface ClassificationState {
  recentFrames: Record<string, number>[];
  pendingEmotion: string;
  pendingEmotionCount: number;
  committedEmotion: string;
  committedEmotionHold: number;
  postYawnCooldownFrames: number;
  postEmotionSettlingFrames: number;
  baselineStability: number;
  smoothed: SmoothedState | null;
  /** Per-emotion streak accumulators for mobile pattern persistence. */
  mobileStreaks?: Record<string, number>;
}

export interface ProcessFrameHelpers {
  averagePositiveExcursion: (keys: string[]) => number;
  isWithinNeutralRange: (bs: Record<string, number>) => boolean;
  isEmotionWeakRelativeToBaseline: (bs: Record<string, number>, emotion: string) => boolean;
  decideFocus: (
    winner: EmotionScore,
    handNearFace: boolean,
    handNearMouth: boolean,
    yawnDetected: boolean,
    mouthOpen: boolean,
    stab: number,
    baselineStability: number,
    isPostYawn: boolean,
    blendshapes?: Record<string, number>,
  ) => EmotionScore;
  computeStability: (recentFrames: Record<string, number>[]) => number;
  duchenneFactor: (bs: Record<string, number>) => number;
}

export interface ProcessFrameInput {
  scores: EmotionScore[];
  blendshapes: Record<string, number>;
  rawBlendshapes: Record<string, number>;
  baseline?: {
    eyeContextProfiles?: {
      eyeOccluded?: { auReliability?: Record<string, number> };
    };
    auRanges?: Record<string, { p10: number; p90: number }>;
  } | null;
  adaptiveConfig?: {
    calibrationQuality: number;
    minScoreDeltas?: Partial<Record<'sad' | 'angry' | 'happy' | 'surprise', number>>;
    persistenceOverrides?: {
      sadFrames?: number;
      angryExtraFrames?: number;
      committedHoldFrames?: number;
    };
    strongInstantScoreDeltas?: Partial<Record<'sad' | 'angry' | 'happy' | 'surprise', number>>;
  };
  yawnDetected: boolean;
  yawnScore: number;
  eyeContext: EyeReliabilityContext;
  handNearFace: boolean;
  handNearMouth: boolean;
  mouthOpen: boolean;
  mouthOpenForFocus: boolean;
  lipCurveSad: number;
  mar: number;
  frameId: number;
  isMobileRuntime?: boolean;
  emotionThresholds?: {
    minScoreByEmotion?: Record<string, number>;
    strongInstantScoreByEmotion?: Record<string, number>;
  };
  config: { smoothingFactor: number; persistenceFrames: number };
  state: ClassificationState;
  helpers: ProcessFrameHelpers;
}

/**
 * Stateful wrapper around processEmotionFrame.
 * Owns ClassificationState internally — caller only passes per-frame data.
 */
export class EmotionFrameProcessor {
  private state: ClassificationState = {
    recentFrames: [],
    pendingEmotion: 'neutral',
    pendingEmotionCount: 0,
    committedEmotion: 'neutral',
    committedEmotionHold: 0,
    postYawnCooldownFrames: 0,
    postEmotionSettlingFrames: 0,
    baselineStability: 0,
    smoothed: null,
    mobileStreaks: {},
  };

  process(input: Omit<ProcessFrameInput, 'state'>): any {
    const { nextState, emotionalState } = processEmotionFrame({ ...input, state: this.state });
    this.state = nextState;
    return emotionalState;
  }

  getRecentFrames(): Record<string, number>[] {
    return this.state.recentFrames;
  }

  updateBaselineStability(value: number): void {
    this.state = { ...this.state, baselineStability: value };
  }

  updatePostYawnCooldown(value: number): void {
    this.state = { ...this.state, postYawnCooldownFrames: value };
  }

  getPostYawnCooldown(): number {
    return this.state.postYawnCooldownFrames;
  }

  reset(): void {
    this.state = {
      recentFrames: [],
      pendingEmotion: 'neutral',
      pendingEmotionCount: 0,
      committedEmotion: 'neutral',
      committedEmotionHold: 0,
      postYawnCooldownFrames: 0,
      postEmotionSettlingFrames: 0,
      baselineStability: 0,
      smoothed: null,
      mobileStreaks: {},
    };
  }
}

export function processEmotionFrame(input: ProcessFrameInput): { nextState: ClassificationState; emotionalState: any } {
  const {
    scores,
    blendshapes,
    rawBlendshapes,
    baseline: _unused,
    adaptiveConfig,
    yawnDetected,
    yawnScore,
    eyeContext,
    handNearFace,
    handNearMouth,
    mouthOpen,
    mouthOpenForFocus,
    lipCurveSad,
    mar,
    frameId,
    isMobileRuntime,
    emotionThresholds,
    config,
    state,
    helpers,
  } = input;

  let {
    pendingEmotion,
    pendingEmotionCount,
    committedEmotion,
    committedEmotionHold,
    postYawnCooldownFrames,
    baselineStability,
    recentFrames,
    smoothed,
  } = state;
  let postEmotionSettlingFrames = state.postEmotionSettlingFrames ?? 0;

  const _log = typeof window !== 'undefined' && (window as any)._log ? (window as any)._log : console.log;
  const _vlog = _log;

  // ── Mobile streak framework: reusable accumulate/decay for any emotion ──
  const mobileStreaks: Record<string, number> = { ...(state.mobileStreaks ?? {}) };

  function updateStreak(key: string, matched: boolean, opts?: { decay?: number; cap?: number; shouldDecay?: boolean }): number {
    const prev = mobileStreaks[key] ?? 0;
    const decay = opts?.decay ?? 2;
    const shouldDecay = opts?.shouldDecay ?? true;
    if (matched) {
      const next = prev + 1;
      mobileStreaks[key] = opts?.cap !== undefined ? Math.min(next, opts.cap) : next;
    } else if (shouldDecay) {
      mobileStreaks[key] = Math.max(0, prev - decay);
    }
    return mobileStreaks[key];
  }

  let winner: EmotionScore = { emotion: 'neutral', score: 0, confidence: 0.5, valence: 0, arousal: 0.1 };
  if (frameId !== undefined) {
    _vlog(`[EIE][FRAME] id=${frameId}`);
  }
  let forceNeutralArtifactReset = false;

  try {
    const sig = computeFrameSignals(blendshapes, rawBlendshapes, lipCurveSad, eyeContext);
    const {
      frown, browDown, squintVal,
      eyeWide, mouthPress, mouthRoll, mouthShrugLower,
      mouthStretch, jawOpenVal,
      pitchExcess, pitchDownExcess, lipCurveSadPoseAdjusted,
      poseAdjustedEyeLookDown, poseAdjustedBrowInnerUp,
    } = sig;

    const artifacts = detectArtifacts({ sig, yawnDetected, yawnScore, mar }, updateStreak);
    const {
      strongTrueSadMouth, mouthOpenShrugArtifact, headOnlySadArtifact, yawnLikeSadArtifact,
      squintPressYawnProfile, noGeomYawnHold, durationNoGeometryYawn,
      strongNoGeometryYawn, emergingNoGeometryYawn, effectiveYawnDetected,
    } = artifacts;

    // Scoring ramp & guards are handled exclusively by Scorer.ts.
    // FrameProcessor only handles: artifact guards, winner selection,
    // persistence state machine, mobile streaks, and EMA smoothing.

    if (!effectiveYawnDetected && postYawnCooldownFrames > 0) {
      postYawnCooldownFrames--;
      for (const s of scores) {
        if (s.emotion !== 'neutral' && s.emotion !== 'focused') {
          s.score = 0; s.confidence = 0;
        }
      }
      _log(`[EIE][POST-YAWN] cooldown=${postYawnCooldownFrames} TOTAL BLOCK`);
    }


    const sadNeutralExcursion = helpers.averagePositiveExcursion([
      'mouthFrownLeft', 'mouthFrownRight', 'mouthShrugLower', 'browInnerUp', 'eyeLookDownLeft', 'eyeLookDownRight',
    ]);
    const winnerResult = selectWinner({
      scores, sadNeutralExcursion,
      minScoreDeltas: adaptiveConfig?.minScoreDeltas,
      emotionThresholds,
      headOnlySadArtifact, mouthOpenShrugArtifact, strongTrueSadMouth, yawnLikeSadArtifact,
      postYawnCooldownFrames, yawnScore, frown, browDown, squintVal,
      eyeContext, lipCurveSadPoseAdjusted, mouthShrugLower, jawOpenVal, mar,
      mouthPress, mouthRoll, mouthStretch,
      emergingNoGeometryYawn, squintPressYawnProfile, noGeomYawnHold, durationNoGeometryYawn, strongNoGeometryYawn,
      poseAdjustedEyeLookDown, poseAdjustedBrowInnerUp, pitchExcess, pitchDownExcess, eyeWide,
    }, _vlog);
    winner = winnerResult.winner;
    forceNeutralArtifactReset = winnerResult.forceNeutralArtifactReset;

    recentFrames.push({ ...rawBlendshapes });
    if (recentFrames.length > 20) recentFrames.shift();

    const stab = recentFrames.length >= 20 ? helpers.computeStability(recentFrames) : -1;
    const handThinkingPose = handNearMouth === true && !effectiveYawnDetected && !mouthOpen;
    winner = helpers.decideFocus(winner, !!handNearFace, handThinkingPose, effectiveYawnDetected, mouthOpenForFocus, stab, baselineStability, postYawnCooldownFrames > 0, blendshapes);

    // ── Mobile expression-based emotion discrimination framework ──
    if (isMobileRuntime) {
      winner = applyMobileStreaks(
        { winner, sig, stab, baselineStability },
        updateStreak,
      );
    }

    const persistence = applyPersistence({
      winner, forceNeutralArtifactReset,
      pendingEmotion, pendingEmotionCount,
      committedEmotion, committedEmotionHold,
      postEmotionSettlingFrames,
      isMobileRuntime, eyeContext, yawnScore, mar,
      emotionThresholds, adaptiveConfig, config,
    });
    winner = persistence.winner;
    pendingEmotion = persistence.pendingEmotion;
    pendingEmotionCount = persistence.pendingEmotionCount;
    committedEmotion = persistence.committedEmotion;
    committedEmotionHold = persistence.committedEmotionHold;
    postEmotionSettlingFrames = persistence.postEmotionSettlingFrames;

    const output = computeSmoothedOutput({
      winner, smoothed, smoothingFactor: config.smoothingFactor,
      duchenneFactor: winner.emotion === 'happy' ? helpers.duchenneFactor(blendshapes) : undefined,
      rawBlendshapes, handNearFace: !!handNearFace, handNearMouth: !!handNearMouth,
      yawnScore, effectiveYawnDetected, eyeContext,
    });
    smoothed = output.smoothed;

    const nextState: ClassificationState = {
      recentFrames,
      pendingEmotion,
      pendingEmotionCount,
      committedEmotion,
      committedEmotionHold,
      postYawnCooldownFrames,
      postEmotionSettlingFrames,
      baselineStability,
      smoothed,
      mobileStreaks,
    };

    return { nextState, emotionalState: output.emotionalState };
  } catch (e) {
    return { nextState: state, emotionalState: {
      emotion: 'neutral', valence: 0, arousal: 0, confidence: 0,
      timestamp: Date.now(), blendshapes: rawBlendshapes,
    } };
  }
}

